import { useState } from "react";
import LocalityForm from "../../../components/forms/configuration/LocalityForm";
import { Accordion, Alert, Button, Card, Col, Form, FormGroup, Image, Row, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import BodyService from "../../../services/body.service";
import { useEffect } from "react";
import LocalityService from "../../../services/locality.service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import UseAlertConfirm from "../../../hooks/UseAlertConfirm";
import PaginationComponent from "../../../components/PaginationComponent";
import TableHeaderSortComponent from "../../../components/TableHeaderSortComponent";
import APP_CONF from "../../../scripts/constants";
import { useForm } from "react-hook-form";
import Select from "react-select";
import RegionService from "../../../services/region.service";
import UserPermissionService from "../../../services/userPermission.service";

const Locality = () => {

    const { t } = useTranslation();

    const [translations, setTranslations] = useState([]);

    const [localities, setLocalities] = useState([]);

    const [region, setRegion] = useState();

    const [regions, setRegions] = useState([]);

    const [permissions, setPermissions] = useState({});

    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalItems: 0,
        itemsPerPage: APP_CONF.ITEMS_PER_PAGE,
        numPages: 1
    });

    const [sortColumn, setSortColumn] = useState({
        sortByColumn: null,
        orderBy: null
    });

    const { showModal } = UseAlertConfirm();

    const [success, setSuccess] = useState({
        create: false,
        delete: false,
    });

    const [errors, setErrors] = useState({
        create: false,
        delete: false,
    });

    const { register, handleSubmit } = useForm({
        mode: "all",
    });

    const [filter, setFilter] = useState({
        active: "",
        search: ""
    })

    const createLocality = async (data) => {
        setErrors({ ...errors, create: false });
        setSuccess({ ...success, create: false });
        try {
            await LocalityService.create(data);
            getEmptyTranslations();
            setSuccess({ ...success, create: true });
            getLocalities();
        } catch (error) {
            console.error(error.response);
            setErrors({ ...errors, create: true });
        }
    };

    const getEmptyTranslations = async () => {
        const data = await BodyService.getEmptyTranslations();
        setTranslations(data);
    };

    const getLocalities = async () => {
        try {
            const data = await LocalityService.getAll(pagination, filter, sortColumn);
            setLocalities(data.content);
            setPagination({
                ...pagination,
                totalItems: data.totalElements,
                numPages: data.totalPages
            });
        } catch (response) {
            if (response?.data?.errorCode) {
                switch (response.data.errorCode) {
                    case 13002:
                        setErrors({ ...errors, email: true });
                        break;
                    default:
                        setErrors({ ...errors, create: true });
                }
            }
        }
    }

    const deleteLocality = async (id) => {
        showModal({
            title: t("general.delete.title"),
            body: t("general.delete.message"),
            confirmButtonVariant: "danger",
            onConfirm: async () => {
                setErrors({ ...errors, create: false });
                setSuccess({ ...success, create: false });
                try {
                    await LocalityService.remove(id);
                    setSuccess({ ...success, delete: true });
                    getLocalities();
                } catch (e) {
                    console.error(e.response);
                    setErrors({ ...errors, delete: true });
                }
            }
        });
    }

    const filterSearch = (data) => {        
        setFilter({
            active: data.active,
            search: data.search,
            regionId: region?.id
        });
    }

    const getRegions = async () => {
        try {
            let emptyRegion = {id:null,name:{"es":{value:""}}};
            const data = await RegionService.getList();           
            setRegions([emptyRegion,...data]);
        } catch (error) {
            console.error(error);
        }
    }

    const getPermissions = async () => {
        try {
            const data = await UserPermissionService.getPermissionsByConnected();
            setPermissions(data);
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        const loadData = async () => {
            await getPermissions();
            await getEmptyTranslations();
        }

        loadData();
    }, []);

    useEffect(() => {
        getLocalities();
        getRegions();
    }, [pagination.currentPage, sortColumn, filter]);

    return (
        <>
            <div className={"d-sm-flex align-items-center justify-content-between mb-4"}>
                <h1 className={"h3 mb-0 text-gray-800"}>{t('locality.title')}</h1>
            </div>

            <Alert variant={"danger"} show={errors.create} dismissible onClose={() => setErrors({ ...errors, create: false })}>
                {t('locality.error.create')}
            </Alert>

            <Alert variant={"success"} show={success.create} dismissible onClose={() => setSuccess({ ...success, create: false })}>
                {t('locality.success.create')}
            </Alert>

            <Alert variant={"success"} show={success.delete} dismissible onClose={() => setSuccess({ ...success, delete: false })}>
                {t('locality.success.delete')}
            </Alert>

            <Alert variant={"danger"} show={errors.delete} dismissible onClose={() => setErrors({ ...errors, delete: false })}>
                {t('locality.error.delete')}
            </Alert>
            {permissions.localities?.read && permissions.localities?.create ? 
            <Card className={"shadow mb-4"}>
                <Accordion>
                    <Accordion.Item eventKey="0">
                        <Accordion.Header><h6 className={"m-0 fw-bold text-primary"}>{t('locality.form.title')}</h6>
                        </Accordion.Header>
                        <Accordion.Body>
                            <LocalityForm onSubmit={createLocality} translations={translations} permissions={permissions} />
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </Card>
            : null }

            <hr />
            <Card className="{shadow mb-4}">
                <Card.Header className={"py-3"}>
                    <h6 className={"m-0 fw-bold text-primary"}>{t('locality.table.title')}</h6>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleSubmit(filterSearch)}>
                        <Row>
                            <Col lg="2" className={"mb-3"}>
                                <FormGroup>
                                    <Form.Label>{t('locality.filter.active.title')}</Form.Label>
                                    <Form.Select {...register('active')} aria-label="Default select example">
                                        <option value="">{t('locality.filter.active.value1')}</option>
                                        <option value={true}>{t('locality.filter.active.value2')}</option>
                                        <option value={false}>{t('locality.filter.active.value3')}</option>
                                    </Form.Select>
                                </FormGroup>
                            </Col>
                            <Col lg="3" className={"mb-3"}>
                                <FormGroup>
                                    <Form.Label>{t('locality.filter.search')}</Form.Label>
                                    <Form.Control type="text" {...register("search")} />
                                </FormGroup>
                            </Col>
                            <Col md={3}>
                                <FormGroup>
                                    <Form.Label>{t('locality.filter.region')}</Form.Label>
                                    <Select options={regions}
                                        classNamePrefix="select"
                                        placeholder=""
                                        getOptionLabel={(option) => option.name["es"].value}
                                        getOptionValue={(option) => option.id}
                                        onChange={(data) => {
                                            setRegion(data)
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Button type="submit" className={"mt-3"} disabled={!permissions.localities?.read}>{t('locality.filter.button')}</Button>
                    </Form>
                    <hr />
                    <Table responsive>
                        <thead>
                            <tr>
                                <TableHeaderSortComponent scope={"col"} text={t('locality.table.name')} column={"name"} sortColumn={sortColumn} setSortColumn={setSortColumn} />
                                <TableHeaderSortComponent scope={"col"} text={t('locality.table.region')} column={"region"} sortColumn={sortColumn} setSortColumn={setSortColumn} />
                                <TableHeaderSortComponent scope={"col"} text={t('locality.table.active')} column={"active"} sortColumn={sortColumn} setSortColumn={setSortColumn} />
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                localities ? localities.map((locality, index) => (
                                    <tr key={index}>
                                        <td>
                                            <div className="d-flex flex-row flex-nowrap">
                                                {
                                                    Object.values(locality.name).map((translation, i) =>
                                                        <div key={i} className="me-2">
                                                            <Image src={translation.language.icon} className="me-2 flags-img-sm" />
                                                            {i + 1 === Object.values(locality.name).length ? translation.value : translation.value + ' | '}
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        </td>
                                        <td>{locality?.region?.name["es"].value}</td>
                                        <td>{locality?.active ? 'Sí' : 'No'}</td>
                                        <td className={"text-center"}>
                                            <Button variant="link" size="sm" className="ms-2" to={`/configuration/locality/${locality.id}`} as={Link} disabled={!permissions.localities?.read}>
                                                <FontAwesomeIcon icon="edit" />
                                            </Button>
                                            <Button variant="link" size="sm" className="text-danger" onClick={() => deleteLocality(locality.id)} disabled={!permissions.localities?.delete}>
                                                <FontAwesomeIcon icon="trash" />
                                            </Button>
                                        </td>
                                    </tr>
                                )) :
                                    <tr>
                                        <td colSpan={4}>{t('general.empty')}</td>
                                    </tr>
                            }
                        </tbody>
                    </Table>
                    <PaginationComponent pagination={pagination}
                        setPagination={setPagination}
                        alwaysShown={localities.length > 0} />
                </Card.Body>
            </Card>
        </>
    );
};

export default Locality;